/**
 * AuthContext
 *
 * Provides a global state to manage authentication-related UI behavior, specifically
 * the visibility of the "Leave Auth Route" confirmation popup.
 *
 * Context Value:
 * - showLeaveAuthRoutePopup: boolean
 *      Indicates whether the "Leave Auth Route" popup is currently visible.
 * - changeShowLeaveAuthRoutePopupAuthState: (status: boolean) => void
 *      Function to update the popup visibility state.
 *
 * Usage:
 * 1. Wrap your component tree with the AuthProvider to provide context:
 *
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 *
 * 2. Consume the context in any child component using the hook:
 *
 * const { showLeaveAuthRoutePopup, changeShowLeaveAuthRoutePopupAuthState } = useAuthContext();
 *
 * Notes:
 * - Throws an error if `useAuthContext` is called outside of `AuthProvider`.
 * - Initial popup visibility is set to true.
 */

import React, { createContext, useContext, useState } from "react";

interface AuthContextType {
  showLeaveAuthRoutePopup: boolean;
  changeShowLeaveAuthRoutePopupAuthState: (status: boolean) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [showLeaveAuthRoutePopup, setShowLeaveAuthRoutePopup] = useState(true);
  const changeShowLeaveAuthRoutePopupAuthState = (status: boolean) =>
    setShowLeaveAuthRoutePopup(status);

  return (
    <AuthContext.Provider
      value={{
        showLeaveAuthRoutePopup,
        changeShowLeaveAuthRoutePopupAuthState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
